import { type RouteRecordRaw } from 'vue-router'

import type { FeatureKey } from '@/config/portal'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import BlankLayout from '@/layouts/BlankLayout.vue'
// PAGES
import HomeView from '@/views/home/HomeView.vue'
import DocumentListView from '@/views/document/DocumentListView.vue'
import SoftwareListView from '@/views/software/SoftwareListView.vue'
import ReferenceDesignListView from '@/views/hardware/ReferenceDesignListView.vue'
import TrainingMaterialListView from '@/views/training/TrainingMaterialListView.vue'
import LinkBudgetCalculator from '@/views/link_budget/LinkBudgetCalculator.vue'
import LegalView from '@/views/legal/LegalView.vue'
import LoginView from '@/views/auth/LoginView.vue'
import Auth0Callback from '@/views/auth/Auth0Callback.vue'
import MicrosoftCallback from '@/views/auth/MicrosoftCallback.vue'
import NotFoundView from '@/views/errors/404NotFound.vue'
import ArticleListView from '@/views/knowledge_base/ArticleListView.vue'

// SD-2-04: a route may declare the portal feature it belongs to. Routes tagged
// with a feature the active portal does not enable are redirected to home by the
// nav guard (src/router/guards.ts). Routes stay REGISTERED so named links always
// resolve — availability is enforced at navigation, not by unregistering routes.
//
// For a route with a `feature`, `document.title` is resolved through the portal
// layer (`resolvePageTitle`) so a portal can reword the page; the `title` below is
// the Peraso-default wording and is kept in step with the catalog's
// `defaultPageTitle` by a test in src/config/portal/__tests__/portal.spec.ts.
declare module 'vue-router' {
  interface RouteMeta {
    /** Portal feature this route belongs to; gated by the nav guard when disabled. */
    feature?: FeatureKey
  }
}

/*
  meta: {
    layout: DefaultLayout           use to dynamically control the layout used (default is DefaultLayout)
    permissions: [                  control pages based on user permission
      'accounts.view_user',
      'accounts.add_user'
    ]
    title: 'title'                  the name showed in menu and breadcrumb
    icon: 'icon-name'               the icon showed in the sidebar
    hidden: true                    controls visibility of menu in sidebar (default is false)
    public: true                    route can be accessed without user authentication (default is false/private)
  }
*/

// Routes that are always available
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: '/',
    meta: {
      layout: DefaultLayout,
      title: 'Home'
    },
    component: HomeView
  },
  {
    path: '/documents',
    name: 'documents',
    meta: {
      layout: DefaultLayout,
      title: 'Product Documents',
      feature: 'documents'
    },
    component: DocumentListView
  },
  {
    path: '/software',
    name: 'software',
    meta: {
      layout: DefaultLayout,
      title: 'Legacy Software Packages',
      feature: 'software'
    },
    component: SoftwareListView
  },
  {
    path: '/hardware',
    name: 'hardware',
    meta: {
      layout: DefaultLayout,
      title: 'Reference Design Kits',
      feature: 'hardware'
    },
    component: ReferenceDesignListView
  },
  {
    path: '/training',
    name: 'training',
    meta: {
      layout: DefaultLayout,
      title: 'Training Materials',
      feature: 'training'
    },
    component: TrainingMaterialListView
  },
  {
    path: '/link-budget',
    name: 'link-budget',
    meta: {
      layout: DefaultLayout,
      title: 'Link Budget Calculator',
      feature: 'link_budget'
    },
    component: LinkBudgetCalculator
  },
  {
    path: '/knowledge-base',
    name: 'knowledge-base',
    meta: {
      layout: DefaultLayout,
      title: 'Knowledge Base',
      feature: 'knowledge_base'
    },
    component: ArticleListView
  },
  {
    path: '/legal',
    name: 'legal',
    meta: {
      layout: DefaultLayout,
      title: 'Legal',
      feature: 'legal'
    },
    component: LegalView
  },
  {
    path: '/login',
    name: 'login',
    meta: {
      layout: BlankLayout,
      title: 'Auth',
      public: true
    },
    component: LoginView
  },
  {
    path: '/callback',
    name: 'callback',
    meta: {
      layout: BlankLayout,
      title: 'Callback',
      public: true
    },
    component: Auth0Callback
  },
  {
    path: '/microsoft-callback',
    name: 'microsoft-callback',
    meta: {
      layout: BlankLayout,
      title: 'Microsoft Callback',
      public: true
    },
    component: MicrosoftCallback
  },
  {
    // Wildcard route for 404 page
    path: '/:catchAll(.*)', // Matches any route that hasn't been defined
    meta: { layout: BlankLayout, hidden: true, public: false },
    component: NotFoundView
  }
]
